import type { ChainNode, ChainPath, ChainTarget, NonEmptyChainPath } from './types';

export const EMPTY_PATHS: readonly ChainPath[] = [];

export function nodeKey(node: ChainNode): string {
  return `${node.kind}:${node.id}`;
}

export function sameNode(left: ChainNode | undefined, right: ChainNode | undefined): boolean {
  return left !== undefined && right !== undefined && nodeKey(left) === nodeKey(right);
}

export function samePath(left: ChainPath, right: ChainPath): boolean {
  return left.length === right.length && left.every((node, index) => sameNode(node, right[index]));
}

export function isPathPrefix(prefix: ChainPath, path: ChainPath): boolean {
  return prefix.length <= path.length && prefix.every((node, index) => sameNode(node, path[index]));
}

/** A saved target is in scope when it and a current scope path share an ancestry prefix. */
export function pathMatchesScope(savedPath: ChainPath, scopePaths: readonly ChainPath[]): boolean {
  return (
    savedPath.length > 0 &&
    scopePaths.some(
      (scopePath) => scopePath.length > 0 && (isPathPrefix(scopePath, savedPath) || isPathPrefix(savedPath, scopePath)),
    )
  );
}

export function targetKey(value: ChainTarget | null | undefined): string {
  return value ? `${value.kind}:${value.id}` : 'none';
}

/** Derives the selectable target represented by a full path. */
export function targetFromPath(path: NonEmptyChainPath): ChainTarget {
  const node = path[path.length - 1];
  if (!node) throw new Error('A chain path must end at a selectable target.');
  return { kind: node.kind, id: node.id };
}

export function targetIdFromPath(path: NonEmptyChainPath): string {
  const target = targetFromPath(path);
  return `${target.kind}-${target.id}`;
}

/** Returns the resolved prefix ending at the target, or an empty path. */
export function resolveTargetPath(value: ChainTarget | null | undefined, path?: ChainPath): ChainPath {
  if (!value || !path) return [];
  const index = path.findIndex((node) => node.kind === value.kind && node.id === value.id);
  return index === -1 ? [] : path.slice(0, index + 1);
}

/**
 * Resolves the selected target to a complete branch from the current results.
 *
 * Saved parent targets (for example, a vendor or outlet) only contain their
 * own prefix. Prefer the longest matching result so the picker can restore
 * that target while also opening one of its available child branches.
 */
export function navigationPathFromResults(
  value: ChainTarget | null | undefined,
  paths: readonly ChainPath[],
): ChainPath {
  return paths.reduce<ChainPath>((longest, path) => {
    if (resolveTargetPath(value, path).length === 0) return longest;
    return path.length > longest.length ? path : longest;
  }, []);
}

/**
 * Keeps as much of the browsed branch as the results still support: the
 * longest prefix of the navigation that exists in the paths, or null when
 * nothing survives.
 */
export function preservesNavigation(navigation: ChainPath, paths: readonly ChainPath[]): ChainPath | null {
  if (navigation.length === 0) return paths[0] ?? null;
  for (let length = navigation.length; length > 0; length -= 1) {
    const prefix = navigation.slice(0, length);
    const matchingPath = paths.find((path) => prefix.every((node, index) => sameNode(node, path[index])));
    if (matchingPath) return prefix;
  }
  return null;
}

export function truncateNavigation(navigation: ChainPath, depth: number, node: ChainNode): ChainPath {
  return [...navigation.slice(0, depth), node];
}

/**
 * Select a node and implicitly open its first matching descendant branch.
 * This preserves the legacy catalogue behaviour: choosing a vendor opens its
 * first outlet, and choosing an outlet/service category opens its products.
 */
export function expandNavigation(
  paths: readonly ChainPath[],
  navigation: ChainPath,
  depth: number,
  node: ChainNode,
): ChainPath {
  const next = truncateNavigation(navigation, depth, node);
  const matchingPath = paths.find(
    (path) =>
      path.length > depth + 1 && path.slice(0, depth + 1).every((ancestor, index) => sameNode(ancestor, next[index])),
  );
  return matchingPath ? matchingPath : next;
}

export function optionsAtDepth(paths: readonly ChainPath[], navigation: ChainPath, depth: number): ChainNode[] {
  const options = new Map<string, ChainNode>();

  paths.forEach((path) => {
    if (path.length <= depth) return;
    for (let index = 0; index < depth; index += 1) {
      if (!sameNode(path[index], navigation[index])) return;
    }
    const node = path[depth];
    options.set(nodeKey(node), node);
  });

  return Array.from(options.values());
}
